import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BarChart3, Trophy, Star, TrendingUp, Info, Users } from "lucide-react";

interface BenchmarkComparisonProps {
  userPayback: number;
  userROI: number;
  propertyCount: number;
  language: string;
}

const industryBenchmarks = {
  paybackPeriod: { average: 4.2, topPerformers: 1.8 },
  yearOneROI: { average: 320, topPerformers: 680 }
};

const propertySizeTiers = [
  { min: 1, max: 5, label: { en: "1-5 properties", sv: "1-5 fastigheter" } },
  { min: 6, max: 20, label: { en: "6-20 properties", sv: "6-20 fastigheter" } },
  { min: 21, max: 100, label: { en: "21-100 properties", sv: "21-100 fastigheter" } },
  { min: 101, max: 500, label: { en: "100+ properties", sv: "100+ fastigheter" } }
];

// Calculate percentile based on payback period (lower is better)
const calculatePercentile = (paybackMonths: number): number => {
  // Distribution based on simulated customer data
  // Payback < 1 month = top 5%, < 2 months = top 15%, < 3 months = top 30%, < 4.2 (avg) = top 50%
  if (paybackMonths <= 0.5) return 1;
  if (paybackMonths <= 1) return 5;
  if (paybackMonths <= 1.5) return 10;
  if (paybackMonths <= 2) return 15;
  if (paybackMonths <= 2.5) return 22;
  if (paybackMonths <= 3) return 30;
  if (paybackMonths <= 3.5) return 40;
  if (paybackMonths <= 4.2) return 50;
  if (paybackMonths <= 5) return 60;
  if (paybackMonths <= 6) return 70;
  if (paybackMonths <= 8) return 85;
  return 95;
};

const PerformanceBadge = ({ paybackMonths, language }: { paybackMonths: number; language: string }) => {
  if (paybackMonths < 2) {
    return (
      <Badge className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white border-0">
        <Trophy className="h-3 w-3 mr-1" />
        {language === 'sv' ? 'Elitnivå' : 'Elite Performer'}
      </Badge>
    );
  } else if (paybackMonths < 3) {
    return (
      <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0">
        <Star className="h-3 w-3 mr-1" />
        {language === 'sv' ? 'Topp 25%' : 'Top 25%'}
      </Badge>
    );
  } else if (paybackMonths < industryBenchmarks.paybackPeriod.average) {
    return (
      <Badge variant="secondary">
        <TrendingUp className="h-3 w-3 mr-1" />
        {language === 'sv' ? 'Över genomsnitt' : 'Above Average'}
      </Badge>
    );
  }
  return null;
};

const BenchmarkComparison = ({ userPayback, userROI, propertyCount, language }: BenchmarkComparisonProps) => {
  const tier = propertySizeTiers.find(t => propertyCount >= t.min && propertyCount <= t.max) || propertySizeTiers[propertySizeTiers.length - 1];
  const isAboveAverage = userPayback < industryBenchmarks.paybackPeriod.average;
  const isTopPerformer = userPayback < industryBenchmarks.paybackPeriod.topPerformers;
  
  // Calculate position on the bar (0-100%)
  const maxMonths = 12;
  const userPosition = Math.min((userPayback / maxMonths) * 100, 100);
  const avgPosition = (industryBenchmarks.paybackPeriod.average / maxMonths) * 100;
  const topPosition = (industryBenchmarks.paybackPeriod.topPerformers / maxMonths) * 100;

  // Calculate user's percentile ranking
  const userPercentile = calculatePercentile(userPayback);

  const tr = {
    title: language === 'sv' ? 'Branschjämförelse' : 'Industry Comparison',
    payback: language === 'sv' ? 'Återbetalningstid' : 'Payback Period',
    you: language === 'sv' ? 'Du' : 'You',
    months: language === 'sv' ? 'mån' : 'mo',
    avgLabel: language === 'sv' ? 'Genomsnitt' : 'Industry Avg',
    topLabel: language === 'sv' ? 'Topp 25%' : 'Top 25%',
    yourRoi: language === 'sv' ? 'Din ROI' : 'Your ROI',
    percentilePrefix: language === 'sv' ? 'Topp' : 'Top',
    percentileSuffix: language === 'sv' ? 'av användare' : 'of users',
    avgTooltip: language === 'sv' 
      ? 'Baserat på aggregerad data från 500+ HostCall-kunder inom gästfrihetsbranschen (2024)'
      : 'Based on aggregated data from 500+ HostCall customers in the hospitality industry (2024)',
    topTooltip: language === 'sv'
      ? 'Top 25% av kunderna med liknande fastighetsstorlek och samtalsvolym'
      : 'Top 25% of customers with similar property size and call volume',
    roiAvgTooltip: language === 'sv'
      ? 'Genomsnittlig ROI år 1 baserat på kunddata från gästfrihetsbranschen'
      : 'Average Year 1 ROI based on hospitality industry customer data',
    roiTopTooltip: language === 'sv'
      ? 'ROI uppnådd av toppresterande 25% av kunder'
      : 'ROI achieved by top-performing 25% of customers'
  };

  return (
    <TooltipProvider>
      <Card className="p-4 bg-muted/30 border-border/50">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-brand-500" />
            <h4 className="font-semibold text-sm">{tr.title}</h4>
            <Badge variant="outline" className="text-[10px]">
              {language === 'sv' ? tier.label.sv : tier.label.en}
            </Badge>
          </div>
          <PerformanceBadge paybackMonths={userPayback} language={language} />
        </div>

        {/* Percentile Ranking Indicator */}
        {userPercentile <= 50 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-3 flex items-center gap-2 bg-brand-50 dark:bg-brand-900/20 rounded-lg px-3 py-2"
          >
            <Users className="h-4 w-4 text-brand-500" />
            <span className="text-sm font-medium text-brand-600 dark:text-brand-400">
              {tr.percentilePrefix} {userPercentile}% {tr.percentileSuffix}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px] text-xs">
                {language === 'sv' 
                  ? 'Din återbetalningstid placerar dig bland de bästa av våra kunder'
                  : 'Your payback period places you among the best of our customers'}
              </TooltipContent>
            </Tooltip>
          </motion.div>
        )}

        {/* Payback Period Comparison Bar */}
        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-3">{tr.payback}</p>
          
          {/* Bar container with benchmark labels above */}
          <div className="relative pt-8 pb-1">
            {/* Benchmark labels positioned above the bar */}
            <div 
              className="absolute top-0 text-[10px] text-muted-foreground whitespace-nowrap flex flex-col items-center"
              style={{ left: `calc(${avgPosition}% + 8px)`, transform: 'translateX(-50%)' }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-0.5 cursor-help">
                    {tr.avgLabel}
                    <Info className="h-2.5 w-2.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-xs">
                  {tr.avgTooltip}
                </TooltipContent>
              </Tooltip>
              <div className="w-px h-2 bg-gray-400/60 mt-0.5" />
            </div>
            <div 
              className="absolute top-0 text-[10px] text-yellow-600 dark:text-yellow-400 whitespace-nowrap flex flex-col items-center"
              style={{ left: `calc(${topPosition}% + 8px)`, transform: 'translateX(-50%)' }}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-0.5 cursor-help">
                    {tr.topLabel}
                    <Info className="h-2.5 w-2.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-xs">
                  {tr.topTooltip}
                </TooltipContent>
              </Tooltip>
              <div className="w-px h-2 bg-yellow-500/70 mt-0.5" />
            </div>

            {/* Main bar with inline scale labels */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground w-4 shrink-0">0</span>
              
              <div className="relative flex-1 h-8 rounded-full overflow-visible">
                {/* Gradient background */}
                <div className="absolute inset-0 bg-gradient-to-r from-green-300 via-yellow-200 to-red-300 dark:from-green-800/40 dark:via-yellow-800/40 dark:to-red-800/40 rounded-full" />
                
                {/* Industry average marker - dashed line */}
                <div 
                  className="absolute top-0 h-full border-l-2 border-dashed border-gray-400/60 z-10"
                  style={{ left: `${avgPosition}%` }}
                />
                
                {/* Top performer marker - dashed line */}
                <div 
                  className="absolute top-0 h-full border-l-2 border-dashed border-yellow-500/70 z-10"
                  style={{ left: `${topPosition}%` }}
                />
                
                {/* User's position indicator with label ABOVE */}
                <motion.div 
                  className="absolute top-1/2 -translate-y-1/2 z-20"
                  style={{ left: `${Math.max(userPosition, 4)}%` }}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.3 }}
                >
                  <div className="relative -translate-x-1/2">
                    {/* Label above the indicator */}
                    <span 
                      className="absolute -top-9 left-1/2 -translate-x-1/2 text-xs font-semibold text-brand-600 dark:text-brand-400 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-md shadow-sm whitespace-nowrap border border-brand-200 dark:border-brand-700"
                    >
                      {tr.you}: {userPayback.toFixed(1)} {tr.months}
                    </span>
                    {/* The indicator dot */}
                    <div className="w-7 h-7 rounded-full bg-brand-500 border-[3px] border-white dark:border-gray-900 shadow-lg flex items-center justify-center">
                      <span className="text-xs font-bold text-white">✓</span>
                    </div>
                  </div>
                </motion.div>
              </div>
              
              <span className="text-[10px] text-muted-foreground w-8 shrink-0 text-right">12 {tr.months}</span>
            </div>
          </div>
        </div>

        {/* ROI Comparison Grid */}
        <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-border/50">
          <div className="p-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-[10px] text-muted-foreground mb-0.5 flex items-center justify-center gap-0.5 cursor-help">
                  {tr.avgLabel}
                  <Info className="h-2.5 w-2.5" />
                </p>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px] text-xs">
                {tr.roiAvgTooltip}
              </TooltipContent>
            </Tooltip>
            <p className="font-semibold text-base">{industryBenchmarks.yearOneROI.average}%</p>
          </div>
          <motion.div 
            className="bg-brand-50 dark:bg-brand-900/20 rounded-md p-1.5 ring-2 ring-brand-200 dark:ring-brand-800"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4, type: "spring" }}
          >
            <p className="text-[10px] text-brand-600 dark:text-brand-400 mb-0.5">{tr.yourRoi}</p>
            <p className="font-bold text-lg text-brand-600 dark:text-brand-400">{userROI.toFixed(0)}%</p>
          </motion.div>
          <div className="p-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-[10px] text-muted-foreground mb-0.5 flex items-center justify-center gap-0.5 cursor-help">
                  {tr.topLabel}
                  <Info className="h-2.5 w-2.5" />
                </p>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[200px] text-xs">
                {tr.roiTopTooltip}
              </TooltipContent>
            </Tooltip>
            <p className="font-semibold text-base text-yellow-600 dark:text-yellow-400">{industryBenchmarks.yearOneROI.topPerformers}%</p>
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
};

export default BenchmarkComparison;
